import { Route, Routes, useNavigate, useParams } from "react-router";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tab from "ds/components/Tab";
import useErrorHandle from "hooks/useErrorHandle";
import { canDeleteVersion } from "shared/Templates/access/canDeleteVersion";
import { BlueprintState } from "types/generated";

import { TemplateActions } from "../TemplatesWorkbench/FeatureGate/types";
import TemplatesFeatureGateCallout from "../TemplatesWorkbench/FeatureGate/Callout";
import BlueprintStateBadge from "../Blueprints/StateBadge";
import { TemplateContext } from "../Template/Context";
import { showCreateVersionDrawer } from "../Template/Versions/CreateVersionDrawer";
import DeprecatedVersionBadge from "../Template/Versions/DeprecatedVersionBadge";
import { showDeprecateConfirmation } from "../Template/Versions/DeprecateConfirmation";
import { showDeleteConfirmation } from "../Template/Versions/DeleteConfirmation";
import Deploy from "./Deploy";
import TestDeploy from "./TestDeploy";
import TemplateVersionDeployments from "./Deployments";
import { showVersionDetailsDrawer } from "./DetailsDrawer";
import VersionTemplateBody from "./TemplateBody";
import useTemplateVersion from "./useTemplateVersion";
import TemplateFeatureGateTooltip from "../TemplatesWorkbench/FeatureGate/Tooltip";

const TemplateVersionPage = () => {
  const navigate = useNavigate();

  const { versionId, templateId } = useParams<{ versionId: string; templateId: string }>();

  const { error, loading, data } = useTemplateVersion(templateId, versionId);

  const handleOpenEditDrawer = () => {
    if (data?.template?.templateVersion) {
      showCreateVersionDrawer({
        version: data?.template?.templateVersion,
        templateId: templateId as string,
      });
    }
  };

  const handleOpenCloneDrawer = () => {
    if (data?.template?.templateVersion) {
      showCreateVersionDrawer({
        version: data?.template?.templateVersion,
        templateId: templateId as string,
        isCloneMode: true,
      });
    }
  };

  const handleOpenDetailsDrawer = () => {
    if (data?.template?.templateVersion) {
      showVersionDetailsDrawer({
        item: data.template.templateVersion,
      });
    }
  };

  useBreadcrumbs(
    [
      {
        title: "Templates Workbench",
        link: "/templates-workbench",
      },
      {
        title: data?.template?.name || "",
        link: `/template/${data?.template?.id}`,
      },
      {
        title: data?.template?.templateVersion?.version || "",
      },
    ],
    [data?.template?.templateVersion?.version]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.template?.templateVersion) {
    return <PageLoading />;
  }

  if (!data?.template?.templateVersion) {
    return <NotFoundPage />;
  }

  const version = data.template.templateVersion;
  const versionSchema = data.templateSchema;
  const isPublished = version.state === BlueprintState.Published;
  const showDeprecateAction = isPublished && !version.deprecatedAt;

  const handleDeprecateClick = () => {
    showDeprecateConfirmation({
      id: version.id,
      versionLabel: `${version.name} - ${version.version}`,
    });
  };

  const handleDeleteClick = () => {
    showDeleteConfirmation({
      version: `${version.name} - ${version.version}`,
      id: version.id,
    }).then(() => navigate(`/template/${data.template.id}`));
  };

  const versionUrl = `/template/${data.template.id}/version/${data.template.templateVersion.id}`;

  const canDeleteVersionResult = canDeleteVersion(data.template, version.deploymentsCount);

  return (
    <TemplateContext.Provider
      value={{
        template: data.template,
        templateUrl: `/template/${data.template.id}`,
      }}
    >
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{version.version}</ViewHeaderTitle>
            <BlueprintStateBadge state={version.state} />
            <DeprecatedVersionBadge deprecatedAt={version.deprecatedAt} />
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
              Details
            </Button>

            <DropdownMenuEllipsis tooltip="Version actions" dotsSize="medium">
              {!isPublished && (
                <TemplateFeatureGateTooltip
                  on={(props) => (
                    <DropdownMenuItem {...props} onAction={handleOpenEditDrawer}>
                      Edit metadata
                    </DropdownMenuItem>
                  )}
                  template={data.template}
                  action={TemplateActions.EditTemplate}
                  tooltipPlacement="left"
                />
              )}

              <TemplateFeatureGateTooltip
                on={(props) => (
                  <DropdownMenuItem {...props} onAction={handleOpenCloneDrawer}>
                    Duplicate
                  </DropdownMenuItem>
                )}
                template={data.template}
                action={TemplateActions.EditTemplate}
                tooltipPlacement="left"
              />

              {showDeprecateAction && (
                <TemplateFeatureGateTooltip
                  on={(props) => (
                    <DropdownMenuItem {...props} danger onAction={handleDeprecateClick}>
                      Deprecate version
                    </DropdownMenuItem>
                  )}
                  template={data.template}
                  action={TemplateActions.EditTemplate}
                  tooltipPlacement="left"
                />
              )}

              {(canDeleteVersionResult.result || !!canDeleteVersionResult.message) && (
                <DropdownMenuItem
                  onAction={handleDeleteClick}
                  danger
                  isDisabled={!canDeleteVersionResult.result}
                  tooltip={canDeleteVersionResult.message}
                  tooltipPlacement="left"
                >
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuEllipsis>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderNavigation>
          <Tab end to={versionUrl} label="Template body" />
          <Tab to={`${versionUrl}/deployments`} label="Deployments" />
        </ViewHeaderNavigation>
      </ViewHeader>
      <TemplatesFeatureGateCallout />
      <Routes>
        <Route
          index
          element={
            <VersionTemplateBody
              version={version}
              versionSchema={versionSchema}
              template={data.template}
            />
          }
        />

        <Route
          path="deployments"
          element={
            <TemplateVersionDeployments
              templateId={data.template.id}
              versionId={version.id}
              template={data.template}
              versionState={version.state}
              versionDeprecatedAt={version.deprecatedAt}
            />
          }
        />

        <Route path="deploy" element={<Deploy />} />

        <Route path="test-deploy" element={<TestDeploy />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </TemplateContext.Provider>
  );
};

export default TemplateVersionPage;
